/**
 * FR-18 / FR-19: share a badge as an image.
 *
 * The card is composed on a canvas at 2x so it stays sharp when a social app scales it: the
 * badge's own artwork on the purple header, its title and description, and the Battle Up logo -
 * the shareable card in the design, minus the modal's buttons.
 *
 * Every badge in the modal goes through here, not just Iron Habit, so nothing about the drawing
 * is streak-specific.
 */

const SCALE = 2;
const CARD_WIDTH = 327 * SCALE;
const CARD_HEIGHT = 462 * SCALE;
const CARD_RADIUS = 16 * SCALE;
const HEADER_HEIGHT = 215 * SCALE;
const BADGE_SIZE = 230 * SCALE;

const HEADER_IMAGE = "/img/achievement-modal.png";
const LOGO_IMAGE = "/img/batteup-logo-2x.png";

export interface ShareBadgeArgs {
  /** The badge artwork, as the modal receives it - usually an absolute URL on the API host */
  badge: string;
  title: string;
  description: string;
  /** Copy that goes with the image where the target app supports text as well */
  shareText?: string;
}

/**
 * Loads an image ready for canvas use. `crossOrigin` is what allows the badge - served from the
 * API host - to be drawn without tainting the canvas, provided the host sends CORS headers.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load ${src}`));
    image.src = src;
  });
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.arcTo(width, 0, width, height, radius);
  ctx.arcTo(width, height, 0, height, radius);
  ctx.arcTo(0, height, 0, 0, radius);
  ctx.arcTo(0, 0, width, 0, radius);
  ctx.closePath();
}

/**
 * Draws centred lines, breaking on words.
 */
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  top: number,
  maxWidth: number,
  lineHeight: number
) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: Array<string> = [];
  let line = "";

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;

    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });

  if (line) {
    lines.push(line);
  }

  lines.forEach((entry, index) => {
    ctx.fillText(entry, CARD_WIDTH / 2, top + index * lineHeight);
  });
}

async function composeCard({
  badge,
  title,
  description,
}: ShareBadgeArgs): Promise<Blob> {
  // Without this the first draw can land before Nunito is ready and fall back to a system font
  await document.fonts?.ready;

  const [header, badgeImage, logo] = await Promise.all([
    loadImage(HEADER_IMAGE),
    loadImage(badge),
    loadImage(LOGO_IMAGE),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas is unavailable");
  }

  roundedRect(ctx, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS);
  ctx.clip();

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  // The header's curved foot is the modal's `clip-path: ellipse(100% 100% at top center)`
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(CARD_WIDTH / 2, 0, CARD_WIDTH, HEADER_HEIGHT, 0, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(header, 0, 0, CARD_WIDTH, HEADER_HEIGHT);
  ctx.restore();

  const badgeRatio = badgeImage.height / badgeImage.width || 1;
  ctx.drawImage(
    badgeImage,
    (CARD_WIDTH - BADGE_SIZE) / 2,
    16 * SCALE,
    BADGE_SIZE,
    BADGE_SIZE * badgeRatio
  );

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#303030";

  ctx.font = `900 ${44 * SCALE}px Nunito, sans-serif`;
  ctx.fillText(title, CARD_WIDTH / 2, 296 * SCALE);

  ctx.font = `600 ${16 * SCALE}px Nunito, sans-serif`;
  drawWrappedText(
    ctx,
    description,
    350 * SCALE,
    CARD_WIDTH - 48 * SCALE,
    20 * SCALE
  );

  const logoWidth = 160 * SCALE;
  const logoHeight = (logo.height / logo.width) * logoWidth;
  ctx.drawImage(
    logo,
    (CARD_WIDTH - logoWidth) / 2,
    CARD_HEIGHT - logoHeight - 36 * SCALE,
    logoWidth,
    logoHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      // A tainted canvas throws here rather than returning null, but guard both
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Could not export the share card"));
      }
    }, "image/png");
  });
}

/**
 * Facebook's web sharer only accepts a URL - it scrapes that page for what to show - so a locally
 * composed image can only reach it through the OS share sheet. That is the first choice; the
 * sharer link is what is left when the browser has no Web Share for files.
 */
function shareToFacebook(text: string) {
  const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    window.location.origin
  )}&quote=${encodeURIComponent(text)}`;

  window.open(url, "_blank", "noopener,noreferrer");
}

export async function shareBadge(args: ShareBadgeArgs): Promise<void> {
  const text = args.shareText ?? `${args.title} - ${args.description}`;

  try {
    const blob = await composeCard(args);
    const file = new File([blob], "battle-up-achievement.png", {
      type: "image/png",
    });

    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: args.title, text });
      return;
    }

    // No Web Share for files: hand the subscriber the image, then open the sharer so they can
    // attach it themselves
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = "battle-up-achievement.png";
    link.click();
    URL.revokeObjectURL(objectUrl);
    shareToFacebook(text);
  } catch (error) {
    // A cancelled share sheet is not a failure worth falling back from
    if (error instanceof DOMException && error.name === "AbortError") {
      return;
    }

    // The badge is served from the API host, so a missing CORS header taints the canvas and the
    // card cannot be exported. Sharing the promotion still works.
    shareToFacebook(text);
  }
}
